import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useJoyPlaceIdBySourceIdLazyQuery } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from 'yet-another-react-lightbox';

export interface GeneratePartnerLinkParams {
  eventId?: string;
  googleId?: string;
  zhId?: string;
  hotelWebsite?: string;
  location?: { lat: number; lng: number };
  name?: string;
  address?: string;
}

export enum PartnerLinkSearchParams {
  googlePlaceId = 'gplid',
  hotelWebsite = 'hotelWebsite',
  latitude = 'lat',
  longitude = 'lng',
  name = 'name',
  address = 'address',
  idType = 'idType',
  source = 'source'
}

export const useHotelBookingPartnerLink = () => {
  const zentrumHubEnabled = useFeatureValue('zentrumHubEnabled').value === 'on';
  const zentrumHubProviderExperiment = useFeatureValue('zentrumHubPartnerEnabled', {
    skip: !zentrumHubEnabled
  }).value;

  const hotelRoute = useHotelRoutePaths();
  const isHotelNewRoutesEnabled = useFeatureValue('hotelNewRoutesEnabled').value === 'on';

  const [joyPlaceIdBySourceId, { data, loading: loadingJoyPlace }] = useJoyPlaceIdBySourceIdLazyQuery({ batchMode: 'fast' });

  const generateUrlPartnerLink = ({ eventId, address, googleId, hotelWebsite, location, name, zhId }: GeneratePartnerLinkParams): string | undefined => {
    if (!eventId) {
      return;
    }

    return withWindow<string | undefined>(window => {
      const destination = new URL(window.location.origin);
      destination.pathname = `/hotel/${zhId || ''}`;

      // Add hotel website
      if (hotelWebsite) {
        destination.searchParams.set(PartnerLinkSearchParams.hotelWebsite, hotelWebsite);
      }

      // Add Google props
      if (googleId) {
        destination.searchParams.set(PartnerLinkSearchParams.googlePlaceId, googleId);
      } else if (name && address) {
        destination.searchParams.set(PartnerLinkSearchParams.name, name);
        destination.searchParams.set(PartnerLinkSearchParams.address, address);
      } else if (location) {
        destination.searchParams.set(PartnerLinkSearchParams.latitude, location.lat.toString());
        destination.searchParams.set(PartnerLinkSearchParams.longitude, location.lng.toString());
      }

      const url = new URL(window.location.origin);
      url.pathname = `/eventredirect/${eventId}`;
      url.searchParams.set('destination', destination.pathname + destination.search);
      return url.toString();
    }, undefined);
  };

  const buildHotelDetailsLinkByZhId = useEventCallback(async (args: GeneratePartnerLinkParams) => {
    if (isHotelNewRoutesEnabled && args?.eventId && args?.zhId) {
      let updatedData = data;
      if (data?.joyPlaceBySourceId?.zhId && data?.joyPlaceBySourceId?.zhId !== args?.zhId) {
        updatedData = undefined;
      }
      joyPlaceIdBySourceId({ variables: { payload: { eventId: args?.eventId, sourceId: args?.zhId, sourceType: 'zh' } } });
      while (loadingJoyPlace) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      const joyPlaceId = updatedData?.joyPlaceBySourceId?.id;
      if (joyPlaceId) {
        const utmQueryParams = 'utm_medium=web&utm_source=website_accommodations_browse_page&utm_campaign=view_hotel';
        return hotelRoute.hotelDetails.goToPath(args?.eventId, joyPlaceId, undefined, utmQueryParams);
      }
    }
    return generateUrlPartnerLink(args);
  });

  const generateHotelId = (googleId?: string, zhId?: string) => {
    return zentrumHubProviderExperiment === 'on' ? `g-${googleId}&z-${zhId}` : googleId || '';
  };

  return { generateUrlPartnerLink, generateHotelId, buildHotelDetailsLinkByZhId };
};
